import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import { CommonResponse } from 'models';
import { MenuClassification, MenuCsv, MenuDetail, SearchMenuFilter } from './models/menu';

// state class
export interface MenuState {
  loading: boolean;
  menu_details: MenuDetail[];
  menu_classification: MenuClassification[];
  is_deleted: boolean;
  is_restored: boolean;
}

// init model state
const initialState: MenuState = {
  loading: false,
  menu_details: [] as Array<MenuDetail>,
  menu_classification: [] as Array<MenuClassification>,
  is_deleted: false,
  is_restored: false,
};

// slice inventory
const menuDetailListSlice = createSlice({
  name: 'menuDetailList',
  initialState,
  reducers: {
    fetchListOfMenuDetail(state, action: PayloadAction<SearchMenuFilter>) {
      state.loading = true;
    },
    fetchListOfMenuDetailSuccess(state, action: PayloadAction<CommonResponse<Array<MenuDetail>>>) {
      state.menu_details = action.payload.data;
      state.loading = false;
    },
    fetchListOfMenuDetailFailed(state) {
      state.loading = false;
      state.menu_details = [];
    },
    deleteMenu(state, action: PayloadAction<Array<number>>) {
      state.loading = true;
    },
    deleteMenuSuccess(state, action: PayloadAction<boolean>) {
      state.loading = false;
      state.is_deleted = action.payload;
    },
    deleteMenuFailed(state) {
      state.loading = false;
      state.is_deleted = false;
    },
    restoreMenu(state, action: PayloadAction<number>) {
      state.loading = true;
    },
    restoreMenuSuccess(state, action: PayloadAction<boolean>) {
      state.loading = false;
      state.is_restored = action.payload;
    },
    restoreMenuFailed(state) {
      state.loading = false;
      state.is_restored = false;
    },
    fetchMenuClassification(state, action: PayloadAction<void>) {
      state.loading = true;
    },
    fetchMenuClassificationSuccess(state, action: PayloadAction<CommonResponse<Array<MenuClassification>>>) {
      state.menu_classification = action.payload.data;
      state.loading = false;
    },
    fetchMenuClassificationFailed(state) {
      state.loading = false;
      state.menu_classification = [];
    },
    postMenu(state, action: PayloadAction<Array<MenuCsv>>) {
      state.loading = true;
    },
    postMenuSuccess(state, action: PayloadAction<CommonResponse<null>>) {
      state.loading = false;
    },
    postMenuFailed(state, action: PayloadAction<CommonResponse<null>>) {
      state.loading = false;
    },
  },
});

// Actions
export const menuListActions = menuDetailListSlice.actions;

// Selectors
export const selectMenuDetailList = (state: RootState) => state.menuDetailList.menu_details;
export const selectMenuClassification = (state: RootState) => state.menuDetailList.menu_classification;

export const selectClassificationOptions = createSelector(selectMenuClassification, (classifications) => {
  return classifications.map((classification: MenuClassification) => ({
    value: classification.cd,
    label: classification.name,
  }));
});
export const selectIsDeleted = (state: RootState) => state.menuDetailList.is_deleted;
export const selectIsRestored = (state: RootState) => state.menuDetailList.is_restored;

// Reducer
export const menuDetailListReducer = menuDetailListSlice.reducer;
